/**
 * PlaythroughGate - Route wrapper that requires playthrough terms acceptance.
 * 
 * Used to gate access to High Play and Live Experience pages.
 * Shows a branded acceptance modal that users must agree to before proceeding.
 * If the user declines, they are redirected to the lobby.
 * 
 * Also used for direct game links — if a game has custom playthrough and the
 * user hasn't accepted terms, they are redirected to the appropriate section page
 * where the gate will trigger.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Zap, Radio, ChevronRight, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlaythroughTermsStatus, useAcceptPlaythroughTerms } from "@/hooks/queries/usePlaythroughTerms";

interface PlaythroughGateProps {
  /** The section being gated: 'high_play' or 'live' */
  section: 'high_play' | 'live';
  /** Child content to render once terms are accepted */
  children: React.ReactNode;
}

/**
 * Section-specific branding configuration
 */
const SECTION_CONFIG = {
  high_play: {
    title: "Welcome to High Play",
    subtitle: "Built for those who love high-stakes play",
    description: "Plinko, mine games, instant wins, and more — high-energy games with bigger potential rewards, designed for players who want to push their luck.",
    playthroughNote: "High Play uses an enhanced playthrough structure on promotional SC only in this section. A portion of your winnings goes toward meeting your playthrough requirement before becoming redeemable.",
    icon: Swords,
    iconColor: "text-purple-400",
    accentColor: "from-purple-500 to-purple-700",
    bgGlow: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    ctaText: "Let's Go",
  },
  live: {
    title: "Welcome to Live Experience",
    subtitle: "Built for the live action thrill",
    description: "Real-time tables, live crash, fortune wheels, and more — immersive games with live dealers streaming 24/7, for players who crave the real casino energy.",
    playthroughNote: "Live Experience uses an enhanced playthrough structure on promotional SC only in this section. A portion of your winnings goes toward meeting your playthrough requirement before becoming redeemable.",
    icon: Radio,
    iconColor: "text-red-400",
    accentColor: "from-red-500 to-red-700",
    bgGlow: "bg-red-500/10",
    borderColor: "border-red-500/30",
    ctaText: "Let's Play",
  },
};

export function PlaythroughGate({ section, children }: PlaythroughGateProps) {
  const navigate = useNavigate();
  const { data: termsStatus, isLoading } = usePlaythroughTermsStatus(section);
  const acceptMutation = useAcceptPlaythroughTerms(section);
  const [isAccepting, setIsAccepting] = useState(false);

  const config = SECTION_CONFIG[section];
  const IconComponent = config.icon;

  // While loading, render nothing (prevents flash)
  if (isLoading) {
    return null;
  }

  // Fail-open: if the endpoint failed, allow access rather than blocking
  if (!termsStatus?.success) {
    return <>{children}</>;
  }

  // If already accepted, render children directly
  if (termsStatus?.accepted) {
    return <>{children}</>;
  }

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await acceptMutation.mutateAsync();
    } catch {
      // Silently fail — user can try again
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/home', { replace: true });
  };

  return (
    <>
    {/* Manual backdrop — sits below the mobile menu z-index */}
    <div className="fixed inset-0 z-[200] bg-black/80" />
    <Dialog open={true} onOpenChange={() => {}} modal={false}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-0 bg-card [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header with gradient accent */}
        <div className={`bg-gradient-to-r ${config.accentColor} px-5 py-4`}>
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-white/15 backdrop-blur-sm">
              <IconComponent className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogHeader className="space-y-0 text-left">
                <DialogTitle className="text-lg font-bold text-white">
                  {config.title}
                </DialogTitle>
              </DialogHeader>
            </div>
          </div>
          <p className="text-white/80 text-xs mt-1.5">{config.subtitle}</p>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <DialogDescription className="text-muted-foreground text-xs leading-relaxed">
            {config.description}
          </DialogDescription>

          {/* Playthrough info card */}
          <div className={`rounded-lg border ${config.borderColor} ${config.bgGlow} p-3 space-y-2.5`}>
            <div className="flex items-start gap-2">
              <Zap className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-foreground">
                  Playthrough Info
                </p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {config.playthroughNote}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1.5 border-t border-border/30">
              <Info className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Playing with redeemable SC? Your winnings stay <span className="font-semibold text-green-500">100% redeemable</span>. 
                Each game shows its playthrough rate via the SC Info tooltip.
              </p>
            </div>
          </div>

          {/* Fine print */}
          <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
            By continuing, you agree to the playthrough structure for this section. 
            You'll only see this again if the requirements change.
          </p>
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 pb-5 pt-0 flex gap-2.5 sm:gap-2.5">
          <Button
            variant="outline"
            onClick={handleDecline}
            className="flex-1"
          >
            Back to Lobby
          </Button>
          <Button
            onClick={handleAccept}
            disabled={isAccepting}
            className={`flex-1 bg-gradient-to-r ${config.accentColor} hover:opacity-90 text-white border-0`}
          >
            {isAccepting ? 'Processing...' : (
              <>
                I Understand, {config.ctaText}
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
